import {
  ComboboxData,
  Group,
  LoadingOverlay,
  Select,
  Stack
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { FiCalendar, FiFilter } from 'react-icons/fi';
import { dateFormat } from '../../../utilities/dataFormat';
import { useMutation } from '@tanstack/react-query';
import { showNotification } from '../../../utilities/mantineNotification';
import Button from '../../elements/Button';
import { Data } from './ModalFilter';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Loader from '../Loader';
import { HiOutlineDownload } from 'react-icons/hi';
import {
  getDepartement,
  getSection,
  getSubBusinessUnit
} from '../../../services/dashboard';
import {
  BusinessUnit,
  BusinessUnitData,
  DepartementData,
  SectionData,
  SubBusinessUnitData
} from '../../../types/businessUnit';
import { periodList } from '../../../constants/defaultValues/comboboxData';

const labelPeriod = 'Select Period';

interface FormFilterProps {
  onFilter: (data: any) => void;
  onReset?: () => void;
  data?: Data;
  type?: 'global' | 'inside-chart';
  downloadable?: boolean;
  loading?: boolean;
  onDownload?: () => void;
  optionBU?: ComboboxData;
  rawBusinessUnit?: BusinessUnit;
}

const FormFilter: React.FC<FormFilterProps> = ({
  downloadable,
  onDownload,
  onFilter,
  onReset,
  loading = false,
  optionBU,
  data,
  type = 'global',
  rawBusinessUnit
}) => {
  const [startDatePeriod, setStartDatePeriod] = useState<string>(
    data?.startDate || null
  );
  const [endDatePeriod, setEndDatePeriod] = useState<string>(
    data?.endDate || null
  );
  const [openCustom, setOpenCustom] = useState<boolean>(false);
  const [optionSubBU, setOptionSubBU] = useState<ComboboxData>([]);
  const [optionDepartement, setOptionDepartement] = useState<ComboboxData>([]);
  const [optionSection, setOptionSection] = useState<ComboboxData>([]);
  const [selectedBusinessUnit, setSelectedBusinessUnit] =
    useState<BusinessUnitData>({
      buCode: '',
      buName: ''
    });
  const [selectedSubBusinessUnit, setSelectedSubBusinessUnit] =
    useState<SubBusinessUnitData>({
      subBuCode: '',
      subBuName: ''
    });
  const [selectedDepartement, setSelectedDepartement] =
    useState<DepartementData>({
      deptCode: '',
      deptName: ''
    });
  const [selectedSection, setSelectedSection] = useState<SectionData>({
    sectionCode: '',
    sectionName: ''
  });
  const [options] = useState<ComboboxData>(periodList);

  const fetchSubBusinessUnit = useMutation({
    mutationFn: () =>
      getSubBusinessUnit({
        BuCode: selectedBusinessUnit.buCode || data?.buCode
      }),
    onError: (error: any) => {
      showNotification({
        type: 'error',
        message: error
      });
    }
  });

  const fetchDepartement = useMutation({
    mutationFn: () =>
      getDepartement({
        buCode: selectedBusinessUnit.buCode || data?.buCode,
        SubBuCode: selectedSubBusinessUnit?.subBuCode
      }),
    onError: (error: any) => {
      showNotification({
        type: 'error',
        message: error
      });
    }
  });

  const fetchSection = useMutation({
    mutationFn: () =>
      getSection({
        buCode: selectedBusinessUnit.buCode || data?.buCode,
        SubBuCode: selectedSubBusinessUnit?.subBuCode || data?.subBuCode,
        DeptCode: selectedDepartement?.deptCode || data?.deptCode
      }),
    onError: (error: any) => {
      showNotification({
        type: 'error',
        message: error
      });
    }
  });

  useEffect(() => {
    if (type === 'global') {
      if (selectedBusinessUnit?.buCode) {
        fetchSubBusinessUnit.mutate();
      }
      if (selectedSubBusinessUnit?.subBuCode) {
        fetchDepartement.mutate();
      }
    }
  }, [selectedBusinessUnit, selectedSubBusinessUnit]);

  useEffect(() => {
    if (type === 'global') {
      if (selectedDepartement?.deptCode) {
        fetchSection.mutate();
      }
    }
  }, [selectedDepartement]);

  useEffect(() => {
    if (optionSubBU.length > 0 && !selectedSubBusinessUnit.subBuCode) {
      const selected: any = optionSubBU.find(
        (option: any) => option.value === data?.subBuCode
      );
      if (selected) {
        setSelectedSubBusinessUnit({
          subBuCode: selected.value,
          subBuName: selected.label
        });
      }
    }
  }, [optionSubBU, data?.subBuCode, selectedSubBusinessUnit.subBuCode]);

  useEffect(() => {
    if (
      optionDepartement.length > 0 &&
      selectedSubBusinessUnit?.subBuCode &&
      !selectedDepartement.deptCode
    ) {
      const selected: any = optionDepartement.find(
        (option: any) => option.value === data?.subBuCode
      );
      if (selected) {
        setSelectedDepartement({
          deptCode: selected.value,
          deptName: selected.label
        });
      }
    }
  }, [
    optionDepartement,
    data?.deptCode,
    selectedSubBusinessUnit?.subBuCode,
    selectedDepartement.deptCode
  ]);

  useEffect(() => {
    if (
      fetchSubBusinessUnit?.data?.data?.data &&
      fetchSubBusinessUnit?.data?.data?.data.length > 0
    ) {
      const filtered = fetchSubBusinessUnit?.data?.data?.data.filter(
        (item: any) => item.subBuCode !== '00000000'
      );
      const data = filtered?.map((item: any) => ({
        label: item.subBuName,
        value: item.subBuCode
      }));
      setOptionSubBU(data);
    }
  }, [fetchSubBusinessUnit?.data?.data?.data]);

  useEffect(() => {
    if (
      fetchDepartement?.data?.data?.data &&
      fetchDepartement?.data?.data?.data.length > 0
    ) {
      const filtered = fetchDepartement?.data?.data?.data.filter(
        (item: any) => item.deptCode !== '00000000'
      );
      const data = filtered?.map((item: any) => ({
        label: item.deptName,
        value: item.deptCode
      }));
      setOptionDepartement(data);
    }
  }, [fetchDepartement?.data?.data?.data]);

  useEffect(() => {
    if (
      fetchSection?.data?.data?.data &&
      fetchSection?.data?.data?.data.length > 0
    ) {
      const filtered = fetchSection?.data?.data?.data.filter(
        (item: any) => item.sectionCode !== '00000000'
      );
      const data = filtered?.map((item: any) => ({
        label: item.sectionName,
        value: item.sectionCode
      }));
      setOptionSection(data);
    }
  }, [fetchSection?.data?.data?.data]);

  const { control, setValue, handleSubmit } = useForm<any>({});

  const handleSelect = (value: any, field: any) => {
    field.onChange(value);
    const currentDate = dayjs();
    const days = ['7', '28'];
    const months = ['3', '6', '12'];
    const inDays = days.includes(value);
    const inMonths = months.includes(value);

    if (inDays || inMonths) {
      setOpenCustom(false);

      const _startDate = dayjs(currentDate)
        .subtract(Number(value), inDays ? 'day' : 'month')
        .format(dateFormat('filter-format'));
      const _endDate = dayjs(currentDate).format(dateFormat('filter-format'));

      setStartDatePeriod(_startDate);
      setEndDatePeriod(_endDate);
      setValue('startDate', _startDate);
      setValue('endDate', _endDate);
    }
    if (!inDays && !inMonths && value === 'custom') {
      setOpenCustom(true);
    }
  };

  useEffect(() => {
    if (data) {
      const values = Object.values(data).filter((item) => !!item);
      if (values.length > 0) {
        setOpenCustom(true);
        setSelectedBusinessUnit({
          buCode: data?.buCode,
          buName: data?.buName
        });
        setSelectedSubBusinessUnit({
          subBuCode: data?.subBuCode,
          subBuName: data?.subBuName
        });
        setSelectedDepartement({
          deptCode: data?.deptCode,
          deptName: data?.deptName
        });
      }
    }
  }, [data]);

  const handleSubmitForm: SubmitHandler<any> = (values) => {
    onFilter({
      ...values,
      ...selectedBusinessUnit,
      ...selectedSubBusinessUnit,
      ...selectedDepartement,
      ...selectedSection,
      startDate: startDatePeriod,
      endDate: endDatePeriod
    });
  };

  const onSelect = (value: string, field: any, name: string) => {
    if (name === 'businessUnit') {
      setSelectedBusinessUnit({
        buCode: value,
        buName:
          rawBusinessUnit.find((item) => item.buCode === value)?.buName || ''
      });
      setSelectedSubBusinessUnit({
        subBuCode: '',
        subBuName: ''
      });
      setSelectedDepartement({
        deptCode: '',
        deptName: ''
      });
    }
    if (name === 'subBusinessUnit') {
      setSelectedSubBusinessUnit({
        subBuCode: value,
        subBuName:
          fetchSubBusinessUnit?.data?.data?.data.find(
            (item: any) => item.subBuCode === value
          )?.subBuName || ''
      });
    }
    if (name === 'departement') {
      setSelectedDepartement({
        deptCode: value,
        deptName:
          fetchDepartement?.data?.data?.data.find(
            (item: any) => item.deptCode === value
          )?.deptName || ''
      });
    }
    if (name === 'section') {
      setSelectedSection({
        sectionCode: value,
        sectionName:
          fetchSection?.data?.data?.data.find(
            (item: any) => item.sectionCode === value
          )?.sectionName || ''
      });
    }
    // console.log(value);
    field.onChange(value);
  };

  const disabled = () => {
    if (!startDatePeriod || !endDatePeriod) {
      return true;
    }

    return Boolean(!startDatePeriod && !endDatePeriod && !selectedBusinessUnit);
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleSubmitForm)}>
        <Stack gap={8}>
          <Controller
            control={control}
            render={({ field }) => (
              <Select
                label={labelPeriod}
                data={options}
                placeholder={labelPeriod}
                onChange={(e) => handleSelect(e, field)}
              />
            )}
            name="period"
          />
          {openCustom && (
            <Group gap={16} justify="space-between">
              <Controller
                control={control}
                render={({ field }) => (
                  <DatePickerInput
                    flex={1}
                    // dropdownType="modal"
                    label="Start Period"
                    placeholder="Select start period"
                    rightSection={<FiCalendar />}
                    value={
                      startDatePeriod
                        ? new Date(startDatePeriod)
                        : field.value
                        ? new Date(field.value)
                        : null
                    }
                    onChange={(date) => {
                      setStartDatePeriod(
                        dayjs(date).format(dateFormat('filter-format'))
                      );
                      field.onChange(
                        dayjs(date).format(dateFormat('filter-format'))
                      );
                    }}
                    defaultValue={new Date(data?.startDate)}
                    maxDate={new Date()}
                    // {...field}
                  />
                )}
                name="startDate"
              />
              <Controller
                control={control}
                render={({ field }) => (
                  <DatePickerInput
                    flex={1}
                    label="End Period"
                    placeholder="Select end period"
                    rightSection={<FiCalendar />}
                    minDate={new Date(startDatePeriod)}
                    value={
                      endDatePeriod
                        ? new Date(endDatePeriod)
                        : field.value
                        ? new Date(field.value)
                        : null
                    }
                    onChange={(date) => {
                      setEndDatePeriod(
                        dayjs(date).format(dateFormat('filter-format'))
                      );
                      field.onChange(
                        dayjs(date).format(dateFormat('filter-format'))
                      );
                    }}
                    defaultValue={new Date(data?.endDate)}
                    maxDate={new Date()}
                    // {...field}
                  />
                )}
                name="endDate"
              />
            </Group>
          )}
          {type === 'global' && (
            <>
              <Controller
                control={control}
                render={({ field }) => (
                  <Select
                    label={'Select Business Unit'}
                    data={optionBU.filter(
                      (item: any) => item.label !== 'UNCATEGORIZED'
                    )}
                    placeholder={'Select Business Unit'}
                    disabled={loading}
                    onChange={(e) => onSelect(e, field, 'businessUnit')}
                    searchable
                    clearable
                    value={selectedBusinessUnit?.buCode || null}
                  />
                )}
                name="businessUnit"
              />
              <Controller
                control={control}
                render={({ field }) => (
                  <Select
                    label={'Select Sub Business Unit'}
                    data={optionSubBU}
                    placeholder={'Select Sub Business Unit'}
                    disabled={
                      fetchSubBusinessUnit.isLoading ||
                      !selectedBusinessUnit?.buCode ||
                      optionSubBU.length === 0
                    }
                    onChange={(e) => onSelect(e, field, 'subBusinessUnit')}
                    searchable
                    clearable
                    value={selectedSubBusinessUnit?.subBuCode || null}
                  />
                )}
                name="subBusinessUnit"
              />
              <Controller
                control={control}
                render={({ field }) => (
                  <Select
                    label={'Select Departement'}
                    data={optionDepartement}
                    placeholder={'Select Departement'}
                    disabled={
                      fetchDepartement.isLoading ||
                      !selectedBusinessUnit?.buCode ||
                      !selectedSubBusinessUnit?.subBuCode ||
                      optionDepartement.length === 0
                    }
                    onChange={(e) => onSelect(e, field, 'departement')}
                    searchable
                    clearable
                    value={selectedDepartement?.deptCode || null}
                  />
                )}
                name="departement"
              />
              {/* <Controller
                control={control}
                render={({ field }) => (
                  <Select
                    label={'Select Section'}
                    data={optionSection}
                    placeholder={'Select Section'}
                    disabled={
                      fetchSection.isLoading ||
                      !selectedBusinessUnit?.buCode ||
                      !selectedSubBusinessUnit?.subBuCode ||
                      !selectedDepartement?.deptCode ||
                      optionSection.length === 0
                    }
                    onChange={(e) => onSelect(e, field, 'section')}
                    searchable
                    clearable
                    value={selectedSection?.sectionCode || null}
                  />
                )}
                name="section"
              /> */}
            </>
          )}
          <Group justify="flex-end" pt={24}>
            {downloadable && (
              <Button
                type="button"
                variant="transparent"
                leftSection={<HiOutlineDownload />}
                onClick={onDownload}
              >
                Download Data Filter
              </Button>
            )}
            {type === 'global' && (
              <Button
                type="button"
                variant="transparent"
                disabled={disabled()}
                onClick={onReset}
              >
                Reset Filter
              </Button>
            )}
            <Button type="submit" disabled={disabled()}>
              <FiFilter />
              Apply Filter
            </Button>
          </Group>
        </Stack>
      </form>
      <LoadingOverlay visible={loading} />
      <LoadingOverlay
        visible={
          (fetchDepartement.isLoading ||
            fetchSubBusinessUnit.isLoading ||
            loading) &&
          type === 'global'
        }
        loaderProps={{
          children: (
            <Loader
              message={`Fetching ${
                loading
                  ? 'business unit'
                  : fetchSubBusinessUnit.isLoading
                  ? 'sub business unit'
                  : 'departement'
              }...`}
            />
          )
        }}
      />
    </>
  );
};

export default FormFilter;
